import * as React from 'react'
import Select, { ValueType } from '../common/select'
import SearchBar from '../common/search.bar'
import cx from 'classnames'
import * as styles from './filtering.options.styl'
import { useEffect } from 'react'

export enum FilterStates {
  all,
  redItems,
  orangeItems,
  yellowItems
}

export interface SelectFilterOption {
  value?: string
  label?: string
}

export interface FilteringOptionsProps {
  role?: string
  redItemsCount?: (() => number) | number
  orangeItemsCount?: (() => number) | number
  yellowItemsCount?: (() => number) | number
  searchValue?: string
  changeUID?: string
  options?: SelectFilterOption[]
  hasSearchBar?: boolean
  hasObjectTypeFilter?: boolean
  hasIdentityTypeFilter?: boolean
  severityFilter?: boolean
  noHeader?: boolean
  handleTypeChange?: (selectedOption: ValueType<SelectFilterOption>) => void
  handleChange?: ({ filterState: FilterStates, searchValue: string }) => void
  allOptionCallback?: () => void
  redItemsCallback?: () => void
  orangeItemsCallback?: () => void
  yellowItemsCallback?: () => void
  placeholder?: string
  selectedIndicator?: number //added to set selected severity out of current component,
  selectedOption?: ValueType<SelectFilterOption>
  delayedSearch?: boolean
  delayForSearch?: number
}

const FilteringOptions: React.FC<FilteringOptionsProps> = React.memo(
  (props: FilteringOptionsProps) => {
    const {
      role,
      redItemsCount,
      orangeItemsCount,
      yellowItemsCount,
      severityFilter,
      noHeader,
      options = [],
      searchValue: searchValueFromProps,
      selectedOption,
      hasObjectTypeFilter,
      hasIdentityTypeFilter,
      handleChange,
      handleTypeChange,
      allOptionCallback,
      redItemsCallback,
      orangeItemsCallback,
      yellowItemsCallback,
      hasSearchBar,
      changeUID,
      placeholder = 'Object Search',
      selectedIndicator,
      delayedSearch,
      delayForSearch
    } = props

    const [filterState, setFilterState] = React.useState<FilterStates>(
      FilterStates.all
    )
    const [searchValue, setSearchValue] = React.useState<string>(
      searchValueFromProps || ''
    )

    useEffect(() => {
      if (selectedIndicator) {
        setFilterState(selectedIndicator)
      }
    }, [selectedIndicator])

    useEffect(() => {
      setSearchValue(searchValueFromProps || '')
      if (changeUID && typeof handleChange === 'function') {
        handleChange({
          searchValue: searchValueFromProps,
          filterState
        })
      }
    }, [changeUID])

    function changeFilter(state: FilterStates) {
      setFilterState(state)
      if (typeof handleChange === 'function') {
        handleChange({
          filterState: state,
          searchValue
        })
      }
    }

    function onTypeChange(selectedOption: ValueType<SelectFilterOption>) {
      if (handleTypeChange) {
        handleTypeChange(selectedOption)
      }
    }

    function onSearchChange(e) {
      setSearchValue(e.target.value)
      if (typeof handleChange === 'function') {
        handleChange({
          searchValue: e.target.value,
          filterState
        })
      }
    }

    function allOptionClick() {
      changeFilter(FilterStates.all)
      if (allOptionCallback) {
        allOptionCallback()
      }
    }

    function redOptionsClick() {
      changeFilter(FilterStates.redItems)
      if (redItemsCallback) {
        redItemsCallback()
      }
    }

    function orangeOptionsClick() {
      changeFilter(FilterStates.orangeItems)
      if (orangeItemsCallback) {
        orangeItemsCallback()
      }
    }

    function yellowOptionsClick() {
      changeFilter(FilterStates.yellowItems)
      if (yellowItemsCallback) {
        yellowItemsCallback()
      }
    }

    const redCount =
      typeof redItemsCount === 'function' ? redItemsCount() : redItemsCount

    let nameFilter = ''

    const orangeCount =
      typeof orangeItemsCount === 'function'
        ? orangeItemsCount()
        : orangeItemsCount

    const yellowCount =
      typeof yellowItemsCount === 'function'
        ? yellowItemsCount()
        : yellowItemsCount

    if (hasObjectTypeFilter) {
      nameFilter = 'changeObjectType'
    }

    if (hasIdentityTypeFilter) {
      nameFilter = 'changeIdentityType'
    }
    return !noHeader ? (
      <div data-role={role} className={styles.filter}>
        {severityFilter && (
          <div
            role={'filtering_options_all'}
            className={cx(styles.optionWrapper, {
              [styles.active]: filterState === FilterStates.all
            })}
            onClick={allOptionClick}
          >
            <div className={styles.filterAll}>ALL</div>
          </div>
        )}
        {redCount && severityFilter ? (
          <div
            role={'filtering_options_red'}
            className={cx(styles.optionWrapper, {
              [styles.active]: filterState === FilterStates.redItems
            })}
            onClick={redOptionsClick}
          >
            <div className={styles.redItems}>{redCount}</div>
          </div>
        ) : null}
        {orangeCount && severityFilter ? (
          <div
            role={'filtering_options_orange'}
            className={cx(styles.optionWrapper, {
              [styles.active]: filterState === FilterStates.orangeItems
            })}
            onClick={orangeOptionsClick}
          >
            <div className={styles.orangeItems}>{orangeCount}</div>
          </div>
        ) : null}
        {yellowCount && severityFilter ? (
          <div
            role={'filtering_options_yellow'}
            className={cx(styles.optionWrapper, {
              [styles.active]: filterState === FilterStates.yellowItems
            })}
            onClick={yellowOptionsClick}
          >
            <div className={styles.yellowItems}>{yellowCount}</div>
          </div>
        ) : null}
        {hasObjectTypeFilter || hasIdentityTypeFilter ? (
          <Select
            className={cx(nameFilter, styles.selectWrapper)}
            name={nameFilter}
            useAll
            value={selectedOption as any}
            onChange={onTypeChange}
            isClearable={false}
            options={options}
          />
        ) : null}

        {hasSearchBar ? (
          <SearchBar
            role={role ? `${role}-searchBar` : null}
            className={styles.searchBarInFilteringGrid}
            placeholder={placeholder}
            searchValue={searchValue}
            onChange={onSearchChange}
            debounced={delayedSearch}
            debounceWait={delayForSearch}
          />
        ) : null}
      </div>
    ) : null
  }
)

export default FilteringOptions
